import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, Play, Building2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import MappingEditor from '../../components/data-integration/MappingEditor';
import {
  generateNormalizedTable,
  saveColumnMapping,
  updateColumnMapping,
  createNormalizedTable,
  resetNormalizedTable,
  getTableCatalog,
  type ColumnSuggestion,
} from '../../services/data-integration-api';
import { toast } from 'sonner';

interface TableMappingProps {
  tableCatalogId: string;
  targetConnectionId: string;
  onBack?: () => void;
}

export default function TableMapping({ tableCatalogId, targetConnectionId, onBack }: TableMappingProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isIncremental = searchParams.get('incremental') === 'true';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [sourceTable, setSourceTable] = useState<any>(null);
  const [suggestedMappings, setSuggestedMappings] = useState<ColumnSuggestion[]>([]);
  const [currentMappings, setCurrentMappings] = useState<ColumnSuggestion[]>([]);
  const [normalizedTableName, setNormalizedTableName] = useState('');
  const [syncIdentityColumn, setSyncIdentityColumn] = useState('');
  const [syncIdentitySource, setSyncIdentitySource] = useState<'primary_key' | 'manual'>('primary_key');
  const [sourceConnectionId, setSourceConnectionId] = useState('');
  const [normalizedTableId, setNormalizedTableId] = useState<string | null>(null);
  const [creationStatus, setCreationStatus] = useState('');
  const [catalogTables, setCatalogTables] = useState<Array<{ table_name: string; table_comment: string }>>([]);
  const [tenantCode, setTenantCode] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await generateNormalizedTable({ tableCatalogId, targetConnectionId });
        setSourceTable(result.source_table);
        setSourceConnectionId(result.source_connection_id || '');
        // 回填已有的规范表记录
        if (result.existing_normalized_table_id) {
          setNormalizedTableId(result.existing_normalized_table_id);
          setCreationStatus(result.existing_creation_status || 'draft');
          setTenantCode(result.existing_tenant_code || '');
        }
        // 加载型录表列表（用于字典表/外键表选择，分页加载全部）
        try {
          const connId = result.source_connection_id || '';
          let allTables: Array<{ table_name: string; table_comment: string }> = [];
          let page = 1;
          const pageSize = 100; // 云函数最大 pageSize
          let total = Infinity;
          while (allTables.length < total) {
            const catalogResult = await getTableCatalog({ connectionId: connId, page, pageSize });
            total = catalogResult.total;
            allTables = allTables.concat(
              catalogResult.items.map(t => ({ table_name: t.table_name, table_comment: t.table_comment }))
            );
            if (catalogResult.items.length < pageSize) break;
            page++;
          }
          setCatalogTables(allTables);
        } catch (catErr) {
          console.error('[TableMapping] 加载型录表列表失败:', catErr);
        }
        setSuggestedMappings(result.suggested_table.columns);
        setCurrentMappings(result.suggested_table.columns);
        setNormalizedTableName(result.suggested_table.normalized_table_name);
        setSyncIdentityColumn(result.sync_identity_column);
        setSyncIdentitySource(result.sync_identity_column ? 'primary_key' : 'manual');
      } catch (e: any) {
        setError(e.message || '加载失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [tableCatalogId, targetConnectionId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (isIncremental) {
        // 增量模式：仅更新新映射（source_column_name 不为空的项）
        if (!normalizedTableId) throw new Error('增量模式下 normalizedTableId 不能为空');
        const newMappings = currentMappings.filter(m => m.source_column_name && m.mapping_status !== 'auto');
        
        const result = await updateColumnMapping({
          normalizedTableId,
          mappings: currentMappings.map(m => ({
            target_column_name: m.target_column_name,
            source_column_name: m.source_column_name,
            source_data_type: m.source_data_type,
            transform_rule: m.transform_rule,
            mapping_status: m.mapping_status,
            dict_config: m.is_dict_field ? {
              is_dict_field: true,
              dict_table_name: m.dict_table_name,
              dict_type_column: m.dict_type_column,
              dict_type_value: m.dict_type_value,
              dict_code_column: m.dict_code_column,
              dict_value_column: m.dict_value_column,
              dict_companion_column_name: m.dict_companion_column_name,
            } : null,
            fk_config: (m.is_fk_field || m.fk_companion_of) ? {
              is_fk_field: m.is_fk_field,
              fk_type: m.fk_type,
              fk_ref_table: m.fk_ref_table,
              fk_ref_pk_column: m.fk_ref_pk_column,
              fk_field_mappings: m.fk_field_mappings,
              fk_companion_of: m.fk_companion_of,
              fk_ref_value_column: m.fk_ref_value_column,
              fk_companion_suffix: m.fk_companion_suffix,
            } : null,
          })),
        });
        setMessage(`增量映射更新成功 (${result.results.filter(r => r.status === 'updated').length} 个字段)`);
        toast.success('增量映射保存成功');
      } else {
        // 普通全量覆盖模式
        const result = await saveColumnMapping({
          normalizedTableId,
          tableCatalogId,
          sourceConnectionId,
          targetConnectionId,
          normalizedTableName,
          syncIdentityColumn,
          syncIdentitySource,
          tenantCode,
          mappings: currentMappings,
        });
        setNormalizedTableId(result.normalizedTableId);
        setCreationStatus(result.creation_status);
        setMessage(`保存成功 (${result.mapping_count} 个映射)`);
        toast.success('映射保存成功');
      }
    } catch (e: any) {
      setError(e.message || '保存失败');
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!normalizedTableId) {
      setError('请先保存映射关系');
      return;
    }
    if (!window.confirm(`确定在目标库创建表 "${normalizedTableName}"？`)) return;

    setCreating(true);
    setError('');
    setMessage('');
    try {
      const result = await createNormalizedTable(normalizedTableId);
      setCreationStatus(result.creation_status);
      setMessage(`表 "${result.normalized_table_name}" 创建成功 (v${result.schema_version}, ${result.column_count} 列, ${result.index_count} 索引)`);
    } catch (e: any) {
      setError(e.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async () => {
    if (!normalizedTableId) return;
    if (!window.confirm(`确定重置表 "${normalizedTableName}"？将删除同步任务记录并 DROP 目标库物理表，之后可重新修改映射和建表。`)) return;

    setResetting(true);
    setError('');
    setMessage('');
    try {
      const result = await resetNormalizedTable(normalizedTableId);
      setCreationStatus(result.creation_status);
      const parts = [];
      if (result.dropped_table) parts.push('目标表已删除');
      if (result.deleted_sync_tasks > 0) parts.push(`${result.deleted_sync_tasks} 条同步任务已清除`);
      setMessage(`重置成功${parts.length > 0 ? ` (${parts.join('、')})` : ''}，可重新修改映射后建表`);
    } catch (e: any) {
      setError(e.message || '重置失败');
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground">加载映射建议...</div>;
  if (error && !sourceTable) return <div className="p-6 text-destructive">错误: {error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="text-base font-semibold">
            {isIncremental ? '增量映射绑定' : '字段映射'} — {sourceTable?.table_name}
          </h2>
          {isIncremental && <Badge variant="outline" className="bg-blue-50">增量模式</Badge>}
          {sourceTable?.table_comment && (
            <span className="text-muted-foreground">{sourceTable.table_comment}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-1" />
            {saving ? '保存中...' : isIncremental ? '保存增量映射' : '保存映射'}
          </Button>
          {!isIncremental && (
            <Button
              variant={creationStatus === 'created' ? 'secondary' : 'outline'}
              onClick={handleCreate}
              disabled={creating || !normalizedTableId || creationStatus === 'created'}
            >
              {creating ? '创建中...' : creationStatus === 'created' ? '已创建' : '确认创建'}
            </Button>
          )}
          {(creationStatus === 'created' || isIncremental) && normalizedTableId && (
            <>
              {!isIncremental && (
                <Button variant="destructive" size="sm" onClick={handleReset} disabled={resetting}>
                  <RotateCcw className="h-4 w-4" />
                  {resetting ? '重置中...' : '重置表'}
                </Button>
              )}
              <Button onClick={() => navigate(`/data-integration/sync/${normalizedTableId}`)}>
                <Play className="h-4 w-4" />
                执行增量同步
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="p-6">
        {error && <p className="text-destructive text-sm mb-3">{error}</p>}
        {message && <p className="text-[var(--color-success)] text-sm mb-3">{message}</p>}

        <div className="mb-4 flex items-end gap-4 p-3 bg-muted/50 rounded-md border border-border">
          <Building2 className="h-4 w-4 text-muted-foreground mt-1" />
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">租户编码</Label>
            <Input
              type="text"
              value={tenantCode}
              onChange={e => setTenantCode(e.target.value)}
              placeholder="填写该源库对应的租户编码（同步时写入 _tenant_code 列）"
              className="font-mono w-[400px] h-8"
            />
          </div>
          <span className="text-xs text-muted-foreground pb-1">
            同步到目标表时每行填入此值，用于生产写入时匹配租户
          </span>
        </div>

        <MappingEditor
          sourceColumns={sourceTable?.columns || []}
          suggestedMappings={suggestedMappings}
          normalizedTableName={normalizedTableName}
          syncIdentityColumn={syncIdentityColumn}
          onTableNameChange={setNormalizedTableName}
          onSyncIdentityChange={setSyncIdentityColumn}
          onMappingsChange={setCurrentMappings}
          catalogTables={catalogTables}
          sourceConnectionId={sourceConnectionId}
        />
      </div>
    </div>
  );
}
